/**
 * The Connectors page's route contract — parsing and building, and nothing else.
 *
 * Every state of the page is a URL: which tab, and which entry is being pointed at. That is not
 * tidiness, it is the reason the page works at all — a push notification saying "Gmail's access
 * expired" has to land ON Gmail, and returning from a connector's OAuth redirect has to land back
 * where it started. State kept only in React would lose both.
 *
 * Deliberately pure and dependency-free: the native resolvers implement the same contract
 * (`DeepLink.swift`, `RouteResolver.kt`), and this is the shape they mirror. Keep the three in step
 * — see `docs/connectors-page-design.md` §1.
 */

export const CONNECTORS_BASE = '/settings/connectors';

export const CONNECTORS_TABS = ['tools', 'channels'] as const;
export type ConnectorsTab = (typeof CONNECTORS_TABS)[number];

/**
 * Tools, not channels.
 *
 * The page is named for what most people came for, and the tab order follows: connecting a tool is
 * the common errand, while channels are set up once and then forgotten.
 */
export const DEFAULT_CONNECTORS_TAB: ConnectorsTab = 'tools';

/**
 * The channel groups. One diagram node, one section, one slug — the sets are identical, which is the
 * invariant `heroChannelNodes` spells out: a section the diagram never mentions is a section nothing
 * points at. So a group added here gets a node AND a section, or it does not get a slug.
 *
 * `wearables` is the hands-free group — the ways to reach the agent without opening anything: Siri, and
 * so the earbuds it already speaks through today; a watch it does not run on yet. It is deliberately
 * about REACHING the agent, not about a device's data. A wearable whose recordings the agent READS is an
 * ordinary connector and lives on the Tools tab, which is where this section sends you for it — the two
 * halves of "do you work with my wearable?" have different answers and must not be collapsed into one row.
 */
export const CHANNEL_SLUGS = ['symphony-apps', 'whatsapp', 'ai-coders', 'gemini-enterprise', 'wearables'] as const;
export type ChannelSlug = (typeof CHANNEL_SLUGS)[number];

export interface ConnectorsRoute {
  tab: ConnectorsTab;
  /** The entry to scroll to and highlight. Absent means the top of the tab. */
  target?: string;
  /** A WhatsApp group's own screen — the one sub-route that is a screen, not a scroll position. */
  whatsappGroupId?: string;
}

function isTab(value: string | undefined): value is ConnectorsTab {
  return !!value && (CONNECTORS_TABS as readonly string[]).includes(value);
}

/**
 * Parse the path segments after the base into a route.
 *
 * **Never rejects.** A stale or malformed link — an old push, a truncated share, a connector slug
 * that has since been renamed — must land somewhere sensible rather than 404. The worst outcome
 * here is the default tab, which is exactly what a bare visit gets.
 */
export function parseConnectorsRoute(segments: readonly string[]): ConnectorsRoute {
  const [rawTab, target, ...rest] = segments.filter(Boolean).map((s) => s.toLowerCase());

  if (!isTab(rawTab)) {
    // No tab, or something unrecognised where the tab should be. If the first segment looks like a
    // channel group, treat it as a channels target — that rescues a link written without the tab.
    if (rawTab && (CHANNEL_SLUGS as readonly string[]).includes(rawTab)) {
      return { tab: 'channels', target: rawTab };
    }
    return { tab: DEFAULT_CONNECTORS_TAB };
  }

  if (!target) return { tab: rawTab };

  // `channels/whatsapp/group/{id}` — the group's own screen.
  if (rawTab === 'channels' && target === 'whatsapp' && rest[0] === 'group' && rest[1]) {
    return { tab: 'channels', target: 'whatsapp', whatsappGroupId: rest[1] };
  }

  // A `group` segment with no id is a malformed group link (a truncated push, an id that failed to
  // interpolate). Land on the WhatsApp section rather than nowhere — mirrors the native resolvers,
  // which fall back to the hub for exactly this case.
  return { tab: rawTab, target };
}

/** Build the canonical path for a route. The inverse of {@link parseConnectorsRoute}. */
export function connectorsPath(route?: Partial<ConnectorsRoute>): string {
  const tab = route?.tab ?? DEFAULT_CONNECTORS_TAB;
  if (route?.whatsappGroupId) {
    return `${CONNECTORS_BASE}/channels/whatsapp/group/${route.whatsappGroupId}`;
  }
  if (route?.target) return `${CONNECTORS_BASE}/${tab}/${route.target}`;
  return `${CONNECTORS_BASE}/${tab}`;
}

/**
 * The DOM id of the element a target scrolls to.
 *
 * Shared by the builder and the page so a route can never point at an id the page doesn't render —
 * the failure mode is silent (nothing scrolls, nothing highlights, the click looks broken).
 */
export function connectorsTargetElementId(tab: ConnectorsTab, target: string): string {
  return `row-${tab}-${target}`;
}
